import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from "react"
import { UserLanguages } from "@models/user"
import { getUserLanguages } from "src/services/api/user"
import ExfluencyLoader from "src/components/ExfluencyLoader"
import { UserContext } from "./UserContext"
import { useTranslation } from "react-i18next"
import { Language, sortByName } from "src/utils/useLanguageList"

interface UserLanguagesContextType {
  userLanguages: UserLanguages
  setUserLanguages: React.Dispatch<React.SetStateAction<UserLanguages | undefined>>
  isLanguagesConfigured: boolean
  allUserLanguages: Language[]
  companyAllowedLanguages: Language[]
  nativeLanguage: Language | null
  additionalLanguage: Language | null
  aiTextTranslatorDefaultLangFrom: Language
  aiTextTranslatorDefaultLangTo: Language
  aiFileTranslatorDefaultLangFrom: Language
  aiFileTranslatorDefaultLangTo: Language
  aiFileTranslatorOutsourcingDefaultLangFrom: Language
  aiFileTranslatorOutsourcingDefaultLangTo: Language
  kmDefaultLang: Language
  nativeLanguages: Language[]
  mainLanguages: Language[]
  additionalLanguages: Language[]
}

const UserLanguagesContext = createContext<UserLanguagesContextType | undefined>(undefined)

export const UserLanguagesProvider = ({ children }: { children: ReactNode }) => {
  const [userLanguages, setUserLanguages] = useState<UserLanguages>()
  const { companyPortalProperties } = useContext(UserContext)
  const { t } = useTranslation("lang")

  const onlyUserLanguages = useMemo(() => {
    return userLanguages?.nativeLanguages
      .concat(userLanguages?.mainLanguages)
      .concat(userLanguages?.additionalLanguages)
  }, [userLanguages])

  const isLanguagesConfigured = (onlyUserLanguages?.length ?? 0) > 0

  const companyAllowedLanguages = useMemo(() => {
    return companyPortalProperties.allowedLanguageList
      .map((l) => l.toLowerCase())
      .map((l) => ({ languageId: l, name: t(l) }))
      .sort((a, b) => sortByName(a, b))
  }, [companyPortalProperties])

  const allUserLanguages = useMemo(() => {
    const allUserLanguageIds =
      onlyUserLanguages?.length && onlyUserLanguages?.length == 0
        ? companyPortalProperties?.allowedLanguageList
        : [...new Set(onlyUserLanguages)]

    return allUserLanguageIds.map((l) => ({ languageId: l, name: t(l) })).sort((a, b) => sortByName(a, b))
  }, [userLanguages])

  const nativeLanguage = useMemo(() => {
    const nativeLanguageId =
      userLanguages?.nativeLanguages?.length && userLanguages?.nativeLanguages?.length > 0
        ? userLanguages?.nativeLanguages[0].toLowerCase()
        : null
    return nativeLanguageId ? { languageId: nativeLanguageId, name: t(nativeLanguageId) } : null
  }, [userLanguages])

  const additionalLanguage = useMemo(() => {
    const additionalLanguageId =
      userLanguages?.additionalLanguages?.length && userLanguages?.additionalLanguages?.length > 0
        ? userLanguages?.additionalLanguages[0].toLowerCase()
        : null
    return additionalLanguageId ? { languageId: additionalLanguageId, name: t(additionalLanguageId) } : null
  }, [userLanguages])

  const aiTextTranslatorDefaultLangFrom = useMemo(() => {
    const defaultLangFromId = companyPortalProperties.defaultLanguageCombinationTranslateAndCreateFrom.toLowerCase()
    return additionalLanguage || { languageId: defaultLangFromId, name: t(defaultLangFromId) }
  }, [companyPortalProperties, additionalLanguage])

  const aiTextTranslatorDefaultLangTo = useMemo(() => {
    const defaultLangToId = companyPortalProperties.defaultLanguageCombinationTranslateAndCreateTo.toLowerCase()
    return nativeLanguage || { languageId: defaultLangToId, name: t(defaultLangToId) }
  }, [companyPortalProperties, nativeLanguage])

  const companyDefaultLangFileFrom = useMemo(() => {
    const defaultLangFromId = companyPortalProperties.defaultLanguageCombinationFileFrom.toLowerCase()
    return { languageId: defaultLangFromId, name: t(defaultLangFromId) }
  }, [companyPortalProperties])

  const companyDefaultLangFileTo = useMemo(() => {
    const defaultLangToId = companyPortalProperties.defaultLanguageCombinationFileTo.toLowerCase()
    return { languageId: defaultLangToId, name: t(defaultLangToId) }
  }, [companyPortalProperties])

  const aiFileTranslatorDefaultLangFrom = useMemo(() => {
    return additionalLanguage || companyDefaultLangFileFrom
  }, [companyPortalProperties, additionalLanguage])

  const aiFileTranslatorDefaultLangTo = useMemo(() => {
    return nativeLanguage || companyDefaultLangFileTo
  }, [companyPortalProperties, nativeLanguage])

  const aiFileTranslatorOutsourcingDefaultLangFrom = useMemo(() => {
    return companyDefaultLangFileFrom || companyAllowedLanguages[0]
  }, [companyPortalProperties])

  const aiFileTranslatorOutsourcingDefaultLangTo = useMemo(() => {
    return companyDefaultLangFileTo || companyAllowedLanguages[0]
  }, [companyPortalProperties])

  const kmDefaultLang = useMemo(() => {
    const defaultLangId = nativeLanguage
      ? nativeLanguage.languageId
      : companyPortalProperties.defaultLanguageCombinationFileFrom ||
        companyPortalProperties.defaultLanguageCombinationTranslateAndCreateFrom ||
        allUserLanguages[0].languageId ||
        "en"

    return { languageId: defaultLangId, name: t(defaultLangId) }
  }, [companyPortalProperties, nativeLanguage])

  useEffect(() => {
    getUserLanguages().then((response) => setUserLanguages(response.data))
  }, [])

  const nativeLanguages = useMemo(() => {
    return userLanguages?.nativeLanguages.map((l) => ({ languageId: l, name: t(l) }))
  }, [userLanguages])

  const mainLanguages = useMemo(() => {
    return userLanguages?.mainLanguages.map((l) => ({ languageId: l, name: t(l) }))
  }, [userLanguages])

  const additionalLanguages = useMemo(() => {
    return userLanguages?.additionalLanguages.map((l) => ({ languageId: l, name: t(l) }))
  }, [userLanguages])

  if (!userLanguages || !allUserLanguages || !nativeLanguages || !mainLanguages || !additionalLanguages) {
    return <ExfluencyLoader />
  }

  return (
    <UserLanguagesContext.Provider
      value={{
        userLanguages,
        setUserLanguages,
        isLanguagesConfigured,
        allUserLanguages,
        companyAllowedLanguages,
        nativeLanguage,
        additionalLanguage,
        aiTextTranslatorDefaultLangFrom,
        aiTextTranslatorDefaultLangTo,
        aiFileTranslatorDefaultLangFrom,
        aiFileTranslatorDefaultLangTo,
        aiFileTranslatorOutsourcingDefaultLangFrom,
        aiFileTranslatorOutsourcingDefaultLangTo,
        kmDefaultLang,
        nativeLanguages,
        mainLanguages,
        additionalLanguages
      }}
    >
      {children}
    </UserLanguagesContext.Provider>
  )
}

export const useLanguages = () => {
  const context = useContext(UserLanguagesContext)
  if (context === undefined) {
    throw new Error("useLanguages must be used within a UserLanguagesProvider")
  }
  return context
}

export default UserLanguagesContext
